/**
 * 슬라이드 편집기 좌측 채팅 패널 — 슬라이드와 1:1 바인딩된 chat session.
 *
 * 기존 chat/Session.tsx 의 핵심 흐름을 단순화한 버전:
 *  - Artifact 패널 / Wiki peek / 모델 picker / 첨부 / 검색 등 슬라이드와 무관한 부분 제거
 *  - 메시지 리스트 + 단순 textarea composer + HIL 모달만
 *  - SSE 의 tool_call_output 이 slide.* 이면 ['slide', slideId] React Query invalidate → 우측 캔버스 자동 리렌더
 */
import { HilModal } from '@/components/chat/HilModal';
import { MessageList } from '@/components/chat/MessageList';
import type { StreamSegment } from '@/components/chat/ToolCallChip';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import {
  type AwaitUserInputEvent,
  followChatStream,
  resolveInterruptStream,
  sendChatMessage,
} from '@/lib/chatStream';
import type { InterruptResolveRequest, SkillToolKey } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  sessionId: string;
  slideId: string;
  /**
   * 외부에서 채팅 메시지를 한 번 자동 전송하고 싶을 때 사용. (예: InitialQuestionnaire 의 답변 묶음)
   * 값이 string 으로 들어오면 즉시 sendMutation 시도하고 onPendingExternalMessageConsumed 호출.
   */
  pendingExternalMessage?: string | null;
  onPendingExternalMessageConsumed?: () => void;
  /**
   * await_user_input 이벤트가 도착했을 때 호출 — 외부 (Editor) 가 캔버스에 큰 폼으로 렌더하는 경우 (kind='batch_ask_user').
   * 단일 'ask_user' 는 SlideChatPanel 내부에서 HilModal(embedded) 로 처리한다.
   */
  onActiveInterruptChange?: (
    payload: { interruptId: string; prompt: AwaitUserInputEvent['prompt'] } | null,
  ) => void;
  /**
   * 외부 (BatchQuestionnaire 등) 에서 인터럽트 응답을 보낼 때.
   * 값이 들어오면 resolveInterruptStream 호출 + onExternalResolveConsumed 콜백.
   */
  externalResolve?: {
    interruptId: string;
    body: import('@innermate/shared').InterruptResolveRequest;
  } | null;
  onExternalResolveConsumed?: () => void;
}

const SLIDE_TOOLS: ReadonlySet<SkillToolKey> = new Set([
  'slide.read_deck',
  'slide.read_page',
  'slide.create_page',
  'slide.update_page',
  'slide.set_page_tsx',
  'slide.delete_page',
  'slide.reorder_pages',
  'slide.set_design',
  'slide.set_meta',
  'slide.list_assets',
]);

export function SlideChatPanel({
  sessionId,
  slideId,
  pendingExternalMessage,
  onPendingExternalMessageConsumed,
  onActiveInterruptChange,
  externalResolve,
  onExternalResolveConsumed,
}: Props) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamSegments, setStreamSegments] = useState<StreamSegment[]>([]);
  const [pendingUser, setPendingUser] = useState<{ content: string; fileIds: string[] } | null>(
    null,
  );
  const [activeInterrupt, setActiveInterrupt] = useState<AwaitUserInputEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const sessionQuery = useQuery({
    queryKey: ['chat', 'session', sessionId],
    queryFn: () => api.chatGetSession(sessionId),
    staleTime: 1_000,
  });

  const messages = sessionQuery.data?.messages ?? [];

  // 슬라이드 mutation 발생 시 우측 캔버스 invalidate.
  const onToolCallOutput = useCallback(
    (toolKey: string | undefined) => {
      if (toolKey && SLIDE_TOOLS.has(toolKey as SkillToolKey)) {
        qc.invalidateQueries({ queryKey: ['slide', slideId] });
        qc.invalidateQueries({ queryKey: ['slides', 'list'] });
      }
    },
    [qc, slideId],
  );

  // 도착 SSE → state 업데이트.
  const buildStreamHandlers = useCallback(
    (toolKeyByCallId: Map<string, string>) => ({
      onDelta: (v: string) =>
        setStreamSegments((segs) => {
          const last = segs[segs.length - 1];
          if (last && last.type === 'text') {
            return [...segs.slice(0, -1), { type: 'text', value: last.value + v }];
          }
          return [...segs, { type: 'text', value: v }];
        }),
      onToolCallStart: (ev: { toolCallId: string; toolKey: SkillToolKey }) => {
        toolKeyByCallId.set(ev.toolCallId, ev.toolKey);
        setStreamSegments((segs) => [
          ...segs,
          {
            type: 'toolcall',
            call: {
              id: ev.toolCallId,
              toolKey: ev.toolKey,
              status: 'pending',
              summary: null,
            },
          },
        ]);
      },
      onToolCallOutput: (ev: { toolCallId: string; status: 'ok' | 'error'; summary: string }) => {
        const tk = toolKeyByCallId.get(ev.toolCallId);
        onToolCallOutput(tk);
        setStreamSegments((segs) =>
          segs.map((s) =>
            s.type === 'toolcall' && s.call.id === ev.toolCallId
              ? { ...s, call: { ...s.call, status: ev.status, summary: ev.summary } }
              : s,
          ),
        );
      },
      onAwaitUserInput: (ev: AwaitUserInputEvent) => {
        setActiveInterrupt(ev);
        onActiveInterruptChange?.({ interruptId: ev.interruptId, prompt: ev.prompt });
      },
      onInterruptResolved: () => {
        setActiveInterrupt(null);
        onActiveInterruptChange?.(null);
      },
      onWarning: (msg: string) => setError(msg),
      onDone: () => {
        setIsStreaming(false);
        setStreamSegments([]);
        setPendingUser(null);
        qc.invalidateQueries({ queryKey: ['chat', 'session', sessionId] });
        qc.invalidateQueries({ queryKey: ['slide', slideId] });
      },
    }),
    [onToolCallOutput, qc, sessionId, slideId, onActiveInterruptChange],
  );

  const sendMut = useMutation({
    mutationFn: async (content: string) => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setIsStreaming(true);
      setStreamSegments([]);
      setPendingUser({ content, fileIds: [] });
      setError(null);
      const toolKeyByCallId = new Map<string, string>();
      await sendChatMessage(sessionId, content, {
        ...buildStreamHandlers(toolKeyByCallId),
        signal: ctrl.signal,
      });
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : '전송 실패');
      setIsStreaming(false);
      setStreamSegments([]);
      setPendingUser(null);
    },
  });

  // 외부 메시지 (InitialQuestionnaire 등) 자동 전송 — 한 번만.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sendMut/onPendingExternalMessageConsumed 는 안정 ref 가정 — 메시지 변경 시에만 트리거.
  useEffect(() => {
    if (!pendingExternalMessage) return;
    if (isStreaming || sendMut.isPending) return;
    const msg = pendingExternalMessage;
    onPendingExternalMessageConsumed?.();
    sendMut.mutate(msg);
  }, [pendingExternalMessage]);

  // 외부 (BatchQuestionnaire) 인터럽트 응답 — 한 번만.
  // biome-ignore lint/correctness/useExhaustiveDependencies: externalResolve 변경 시에만 트리거.
  useEffect(() => {
    if (!externalResolve) return;
    const ctrl = new AbortController();
    const toolKeyByCallId = new Map<string, string>();
    const interruptId = externalResolve.interruptId;
    const body = externalResolve.body;
    onExternalResolveConsumed?.();
    void resolveInterruptStream(sessionId, interruptId, body, {
      ...buildStreamHandlers(toolKeyByCallId),
      signal: ctrl.signal,
    });
    setActiveInterrupt(null);
    onActiveInterruptChange?.(null);
  }, [externalResolve]);

  // 페이지 진입 시 진행 중인 run 이 있으면 follow.
  useEffect(() => {
    let canceled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const toolKeyByCallId = new Map<string, string>();
        const result = await followChatStream(sessionId, {
          ...buildStreamHandlers(toolKeyByCallId),
          signal: ctrl.signal,
        });
        if (canceled) return;
        // 진행 중이지 않으면 followChatStream 이 즉시 null 반환할 수도.
        if (result) {
          setIsStreaming(false);
        }
      } catch {
        if (!canceled) setIsStreaming(false);
      }
    })();
    return () => {
      canceled = true;
      ctrl.abort();
    };
  }, [sessionId, buildStreamHandlers]);

  const onSend = () => {
    const content = draft.trim();
    if (!content || isStreaming) return;
    setDraft('');
    sendMut.mutate(content);
  };

  const onInterruptResolve = (body: InterruptResolveRequest) => {
    if (!activeInterrupt) return;
    const ctrl = new AbortController();
    const toolKeyByCallId = new Map<string, string>();
    void resolveInterruptStream(sessionId, activeInterrupt.interruptId, body, {
      ...buildStreamHandlers(toolKeyByCallId),
      signal: ctrl.signal,
    });
    setActiveInterrupt(null);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {sessionQuery.isLoading ? (
          <p className="text-sm text-foreground-muted">불러오는 중…</p>
        ) : (
          <MessageList
            messages={messages}
            isStreaming={isStreaming}
            streamSegments={streamSegments}
            pendingUser={pendingUser}
          />
        )}
      </div>

      {/* batch_ask_user 는 외부 (Editor) 가 캔버스 영역에 렌더 — 여기선 단일 ask_user / skill_select 만. */}
      {activeInterrupt && activeInterrupt.prompt.kind !== 'batch_ask_user' && (
        <div className="border-t border-border bg-surface-muted/30 px-3 py-3">
          <HilModal
            interruptId={activeInterrupt.interruptId}
            prompt={activeInterrupt.prompt}
            onResolve={onInterruptResolve}
            onClose={() => {
              setActiveInterrupt(null);
              onActiveInterruptChange?.(null);
            }}
            variant="embedded"
          />
        </div>
      )}
      {activeInterrupt && activeInterrupt.prompt.kind === 'batch_ask_user' && (
        <div className="border-t border-border bg-surface-muted/30 px-3 py-2 text-xs text-foreground-muted">
          오른쪽 캔버스에서 질문에 답해 주세요.
        </div>
      )}

      {error && (
        <div className="mx-4 mb-2 px-3 py-2 rounded-md bg-destructive/10 text-destructive text-xs">
          {error}
        </div>
      )}

      <form
        className="border-t border-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
      >
        <div className="flex gap-2 items-end">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder="슬라이드를 어떻게 만들까요? 주제·청중·핵심 메시지를 자유롭게 적어주세요."
            rows={3}
            className="flex-1 resize-none rounded-md border border-border bg-surface-muted/40 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
            disabled={isStreaming}
          />
          <Button type="submit" size="icon" disabled={isStreaming || !draft.trim()}>
            <Send className="size-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}
